import { Spin } from 'antd';
import { Key, RowSelectMethod } from 'antd/es/table/interface';
import React, { ReactNode, useContext, useEffect } from 'react';
import { GraphQLTaggedNode } from 'react-relay';
import { OperationType } from 'relay-runtime';

import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';
import {
    ActionsType,
    CustomTableActionType,
    FilterTableParamType,
    ItemTaskType,
    ItemType,
    OnScheduleItemsImport,
    OnTasksImport,
    TableColumnSmxType,
} from '../types';
import ActionBox from './ActionBox';
import ColorBox from './ColorBox';
import ControlPanel from './ControlPanel';
import { actionsForRender } from './functions';
import useProcessedColumns from './hooks/useProcessedColumns';
import TableDbBody from './tableDbBody';
import TableDbPagination from './tableDbPagination';

export interface ITableDb<TQuery extends OperationType> {
    columns: any;
    dataRows: any[];
    count: number;
    loading?: boolean;
    actions?: ActionsType[];
    customActions?: CustomTableActionType[];
    customActionComponents?: ((item: ItemType | ItemTaskType, data: any) => ReactNode)[];
    connectionId: string;
    mutationUpdate?: GraphQLTaggedNode;
    mutationCreate?: GraphQLTaggedNode;
    mutationDelete?: GraphQLTaggedNode;
    sizeOfPage?: string[];
    paramFilter?: FilterTableParamType | {};
    renderDataColumn?: {
        [key: string]: (value: any, record: any) => ReactNode;
    };
    onClickRow?: (data: any, dataRaw: any) => void;
    heightTableBody?: number;
    onSelectionChange?: (
        selectedRowKeys: Key[],
        selectedRows: TQuery[],
        info: {
            type: RowSelectMethod;
        },
    ) => void;
    onSelect?: (record: TQuery, selected: boolean, selectedRows: TQuery[], nativeEvent: Event) => void;
    defaultHiddenColumn?: string[];
    filterFlexible?: string;
    orderBy?: string[];
    onScheduleItemsImport?: OnScheduleItemsImport;
    onTasksImport?: OnTasksImport;
}

const TableDb = <TQuery extends OperationType>({
    columns,
    dataRows,
    actions,
    count,
    connectionId,
    loading = false,
    mutationUpdate,
    mutationCreate,
    mutationDelete,
    sizeOfPage,
    paramFilter,
    renderDataColumn,
    customActions,
    onClickRow,
    heightTableBody,
    onSelectionChange,
    onSelect,
    defaultHiddenColumn,
    filterFlexible,
    orderBy,
    onScheduleItemsImport,
    onTasksImport,
    customActionComponents,
}: ITableDb<TQuery>) => {
    const { nameQuery, prefixStore } = useContext(SmxPropsTableContext);
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));
    const newCheckList = useProcessedColumns<string>(columns, 'checkList');
    const checkedList = useDynamicSelector('table', 'checkedList');

    useEffect(() => {
        setterTable({ columns: columns, dataRows: dataRows, count });
    }, [columns, dataRows, count]);

    useEffect(() => {
        if (!checkedList.length) {
            setterTable({ checkedList: newCheckList.filter((check) => !defaultHiddenColumn?.includes(check)) });
        }
    }, []);

    useEffect(() => {
        setterTable({ flexibleFilters: filterFlexible, orderBy: orderBy });
    }, [filterFlexible, orderBy]);

    const copyColumns = (columns: any) =>
        JSON.parse(JSON.stringify(columns.map((column: any) => ({ ...column, filterParams: '' })))).map(
            (column: TableColumnSmxType, index: string) => ({
                ...column,
                render: columns[index].render,
                sorter: columns[index].sorter,
                children: columns[index].children ? copyColumns(columns[index].children) : undefined,
            }),
        );

    const newColumns = copyColumns(columns);

    useProcessedColumns(newColumns, 'hidden');
    useProcessedColumns(newColumns, 'hiddenParent');

    const editableColumns: TableColumnSmxType[] = newColumns.filter((col: TableColumnSmxType) => col.editable);

    const addRenderForColumn = (columns: TableColumnSmxType[]) => {
        for (const column of columns) {
            const externalRender = renderDataColumn?.[column.key];

            if (externalRender) {
                column.render = externalRender;
                continue;
            }

            switch (column.typeDecode) {
                case 'Color':
                    column.render = (value: string) => <ColorBox color={value} />;
                    break;
                case 'Action':
                    column.render = (value: string, record: any) => {
                        return (
                            <ActionBox
                                actions={actionsForRender(actions || [])}
                                customActions={customActions}
                                customActionComponents={customActionComponents}
                                data={record}
                                editableColumns={editableColumns}
                                connectionId={connectionId}
                                mutationUpdate={mutationUpdate}
                                mutationDelete={mutationDelete}
                            />
                        );
                    };
                    break;
                default:
                    break;
            }
        }
    };

    addRenderForColumn(newColumns);

    return (
        <Spin tip={'Loading ...'} spinning={loading}>
            <ControlPanel
                columns={newColumns}
                dataRows={dataRows}
                actions={actions}
                connectionId={connectionId}
                mutationCreate={mutationCreate}
                editableColumns={editableColumns}
                paramFilter={paramFilter}
                onScheduleItemsImport={onScheduleItemsImport}
                onTasksImport={onTasksImport}
            />
            <TableDbBody
                newColumns={newColumns}
                dataRows={dataRows}
                heightTableBody={heightTableBody}
                onClickRow={onClickRow}
                onSelectionChange={onSelectionChange}
                onSelect={onSelect}
            />
            <TableDbPagination count={count} sizeOfPage={sizeOfPage} />
        </Spin>
    );
};

export default TableDb;
